import { t } from 'ttag'
import { useMemo, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import isFunction from 'lodash/isFunction'

import { PG, SLB } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Alert } from '@h2/components/display/alert'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Link } from '@h2/common/components/link'
import { Card } from '@h2/common/components/card'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'

import { props } from './props'
import { EmailForm as Form } from './components'

import { Create } from '../highlights/create'
import { Navigator } from '../highlights/navigator'
import { Controls } from '../controls'
import { Submit } from '../submit'

const ExternalId = ({
  inDialog,
  properties,
  validate,
  isValidating,
  formErrors,
  serverErrorMsg,
  customDetourHandler
}) => {
  const dispatch = useDispatch()
  const { register, error, isInvalid, handleOnSubmit, setValidation } = useForm({
    initialValues: {
      externalId: properties.externalId ?? undefined
    },
    validators: {
      externalId(form) {
        if (!form.externalId?.trim()) {
          return t`Library Card is required`
        }
      }
    }
  })
  const detourLinkProps = useMemo(
    () => ({
      onClick:
        !inDialog && !isFunction(customDetourHandler)
          ? undefined
          : evt => {
              evt.preventDefault()

              if (isFunction(customDetourHandler)) {
                customDetourHandler()
              } else {
                dispatch(openSigninDialog('signIn'))
              }
            }
    }),
    [dispatch, inDialog, customDetourHandler]
  )
  const onSubmit = useCallback(form => validate(form), [validate])
  const isExplorer = useMemo(() => properties.isExplorer ?? true, [properties.isExplorer])

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])

  return (
    <Card useDetour inDialog={inDialog}>
      <Card.Highlight color={isExplorer ? PG : SLB}>
        {isExplorer ? <Create /> : <Navigator />}
      </Card.Highlight>
      <Card.Content>
        <Form onSubmit={handleOnSubmit(onSubmit)}>
          {serverErrorMsg && (
            <Alert
              type="error"
              message={serverErrorMsg}
              margin="0 0 15px 0"
              width="100%"
            />
          )}
          <Heading level="2" align="center">
            {t`Please enter a Library Card to associate with your account.`}
          </Heading>
          <Field
            isRequired
            width="100%"
            margin="15px 0 0 0"
            label={t`Library Card`}
            desc={error.externalId}
            status={error.externalId ? 'error' : 'default'}>
            <TextInput size="small" {...register('externalId', { required: true })} />
          </Field>
          <Controls useControls>
            <Submit
              text={t`Next`}
              isLoading={isValidating}
              isDisabled={isInvalid || isValidating || Boolean(serverErrorMsg)}
            />
          </Controls>
          <Text margin="25px 0 0 0">
            {t`Need a library card? `}{' '}
            <Link ssr={true} href="/info/nevada/districts">
              {t`Find your library here.`}
            </Link>
          </Text>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Text>
          {t`Already have an account?`}{' '}
          <Link href="/signin" {...detourLinkProps}>{t`Sign in here`}</Link>
        </Text>
      </Card.Detour>
    </Card>
  )
}

ExternalId.propTypes = { ...props }

export { ExternalId }
